import { skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../../../api-client";
import { venueApi } from "../../../venue-api";
import { queryKeys } from "../../query-keys";

/**
 * The club's court-booking state (ADR-0052). 403/404 mean the caller may not
 * see the booking section at all, so they are final — never retried.
 */
export function useClubVenue(clubId: string | undefined) {
	return useQuery({
		queryFn: clubId ? () => venueApi.clubs.get(clubId) : skipToken,
		queryKey: queryKeys.clubPortal.courtBooking(clubId ?? ""),
		retry: (failureCount, error) => {
			if (error instanceof ApiError && (error.status === 403 || error.status === 404)) {
				return false;
			}
			return failureCount < 2;
		},
	});
}

function useClubVenueMutation(clubId: string, mutationFn: () => Promise<unknown>) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn,
		onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.clubPortal.courtBooking(clubId) }),
	});
}

/** Switch court booking on — idempotent on the server, safe to double-click. */
export const useEnableClubVenue = (clubId: string) =>
	useClubVenueMutation(clubId, () => venueApi.clubs.enable(clubId));

/** Pause: no new bookings; existing bookings stay; reversible via resume. */
export const useSuspendClubVenue = (clubId: string) =>
	useClubVenueMutation(clubId, () => venueApi.clubs.suspend(clubId));

export const useResumeClubVenue = (clubId: string) =>
	useClubVenueMutation(clubId, () => venueApi.clubs.resume(clubId));
